import { DataListItem, DataListRoot } from '@/components/ui/data-list';
import { InvitationStatusBadge } from '@/components/ui/invitation-status-badge';
import { formatFirstOrderCommissionSummary } from '@/components/program/first-order-commission';
import { useAuth } from '@/contexts/auth-context';
import { Badge, FormatNumber, Link, SimpleGrid, Text } from '@chakra-ui/react';
import type { InvitationStatus, Program } from '@fluenceur/types';
import { CommissionTypes } from '@fluenceur/types';
import { format } from 'date-fns';
import { Calendar, CalendarCheck, PercentageCircle, ShoppingCode } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

interface InvitationDetailsProps {
  program: Partial<Program>;
  sentDate: Date | null;
  expireDate: Date | null;
  status?: InvitationStatus | null;
}

/**
 * Component to display invitation details including dates, commission and discount code
 */
export const InvitationMessageDetails = ({
  program,
  sentDate,
  expireDate,
  status,
}: InvitationDetailsProps) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const currency = user?.currentBrand?.currency ?? 'EUR';
  const firstOrderSummary = formatFirstOrderCommissionSummary(program.commissionSettings, {
    locale: i18n.language,
    currency,
    t,
  });
  // Format dates from Date objects
  const formatDate = (date: Date | null) => {
    if (!date) return '';
    try {
      return format(date, 'dd MMMM yyyy');
    } catch (error) {
      console.error('Invalid date format:', error);
      return '';
    }
  };

  const renderCommission = () => {
    if (firstOrderSummary) return <Text>{firstOrderSummary}</Text>;
    switch (program.commissionSettings?.type) {
      case CommissionTypes.Percentage:
        return (
          <Badge colorPalette="gray" size="md">
            <PercentageCircle width={18} height={18} strokeWidth={1.5} />
            <FormatNumber
              value={(program.commissionSettings?.value ?? 0) / 100}
              style="percent"
              minimumFractionDigits={0}
              maximumFractionDigits={2}
            />
          </Badge>
        );
      case CommissionTypes.FixedAmountPerOrder:
        return (
          <Badge colorPalette="gray" size="md">
            <ShoppingCode width={18} height={18} strokeWidth={1.5} />
            <FormatNumber
              value={program.commissionSettings?.value ?? 0}
              style="currency"
              currency={currency}
            />
          </Badge>
        );
      default:
        return null;
    }
  };

  const renderAppliedDiscount = () => {
    if (program.discountSettings?.value == null) return null;

    if (program.discountSettings?.type === 'percentage') {
      return (
        <Badge colorPalette="gray" size="md">
          <PercentageCircle width={18} height={18} strokeWidth={1.5} />
          <FormatNumber
            value={(program.discountSettings?.value ?? 0) / 100}
            style="percent"
            minimumFractionDigits={0}
            maximumFractionDigits={2}
          />
        </Badge>
      );
    } else {
      return (
        <Badge colorPalette="gray" size="md">
          <ShoppingCode width={18} height={18} strokeWidth={1.5} />
          <FormatNumber
            value={program.discountSettings?.value ?? 0}
            style="currency"
            currency={currency}
          />
        </Badge>
      );
    }
  };
  return (
    <DataListRoot orientation="vertical" gap={3}>
      <SimpleGrid columns={2} gap={4}>
        <DataListItem
          label="Program"
          value={
            <Link href={`/programs/${program.id}`} colorPalette="lilac" fontWeight="medium">
              {program.name}
            </Link>
          }
        />
        <DataListItem label="Status" value={<InvitationStatusBadge status={status} />} />
      </SimpleGrid>
      <SimpleGrid columns={2} gap={4}>
        <DataListItem
          label="Sent"
          labelIcon={Calendar}
          value={<Text>{formatDate(sentDate)}</Text>}
        />
        <DataListItem
          label="Expire"
          labelIcon={CalendarCheck}
          value={<Text>{formatDate(expireDate)}</Text>}
        />
      </SimpleGrid>

      {(program.commissionSettings?.value != null && program.commissionSettings?.type != null) ||
      program.discountSettings?.value != null ? (
        <SimpleGrid columns={2} gap={4} mt={2}>
          {program.commissionSettings?.value != null &&
            program.commissionSettings?.type != null && (
              <DataListItem label="Commission offer" value={renderCommission()} />
            )}
          {program.discountSettings?.value != null && (
            <DataListItem label="Applied discount" value={renderAppliedDiscount()} />
          )}
        </SimpleGrid>
      ) : null}
    </DataListRoot>
  );
};
